import React, { useEffect, useRef, useState } from 'react';
import { Video, Mic, X, Circle, Camera, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RecordingMode } from '../hooks/useRecording';
import {
  DEFAULT_CAMERA_BUBBLE,
  type CameraBubble,
  type CameraCorner,
  type CameraShape,
  type CameraSize,
} from '../lib/capture/compositor';

interface SetupOptions {
  recordScreen: boolean;
  recordAudio: boolean;
  recordCamera: boolean;
  recordingMode: RecordingMode;
  cameraBubble?: CameraBubble;
  /** Seconds counted down before recording starts (0 = start at once). */
  countdownSeconds?: number;
}

export const COUNTDOWN_OPTIONS = [0, 3, 5] as const;
export const DEFAULT_COUNTDOWN_SECONDS = 3;

interface RecordingSetupPanelProps {
  onClose: () => void;
  recordingOptions: SetupOptions;
  onOptionChange: (options: SetupOptions) => void;
  onStartRecording: () => void;
}

const Toggle: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} aria-label={label} />
    <div
      className="relative w-11 h-6 rounded-full transition-all"
      style={{
        backgroundColor: checked
          ? 'var(--toggle-bg-checked, var(--button-primary-bg, #1976d2))'
          : 'var(--toggle-bg, #e0e0e0)',
        border: 'var(--toggle-border, 1px solid var(--border-color, #e0e0e0))',
      }}
    >
      <span
        className="absolute top-[1px] h-5 w-5 rounded-full transition-all"
        style={{
          left: checked ? 'calc(100% - 21px)' : '1px',
          backgroundColor: 'var(--toggle-thumb-bg, #ffffff)',
          border: 'var(--toggle-thumb-border, 1px solid transparent)',
        }}
      />
    </div>
  </label>
);

const OptionRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs" style={{ color: 'var(--text-secondary, #666666)' }}>{label}</span>
    <div className="flex items-center gap-1">{children}</div>
  </div>
);

const Segment: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode }> = ({
  selected, onClick, children,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className="px-2.5 py-1 rounded text-xs font-medium"
    style={{
      border: selected
        ? '1px solid var(--button-primary-bg, #1976d2)'
        : '1px solid var(--border-color, #e0e0e0)',
      backgroundColor: selected ? 'var(--button-primary-bg, #1976d2)' : 'var(--card-bg, #ffffff)',
      color: selected ? 'var(--button-primary-foreground, #ffffff)' : 'var(--text-primary, #1a1a1a)',
    }}
  >
    {children}
  </button>
);

/** Live camera preview in the chosen shape, so framing can be checked before recording. */
const CameraPreview: React.FC<{ shape: CameraShape }> = ({ shape }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 } }, audio: false })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((track) => track.stop()); return; }
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (failed) {
    return (
      <p className="text-xs" style={{ color: 'var(--theme-error, #dc2626)' }}>
        {t('camera_unavailable', 'Camera not available. Check the browser permission.')}
      </p>
    );
  }
  return (
    <div className="flex justify-center">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="w-24 h-24 object-cover"
        style={{
          borderRadius: shape === 'circle' ? '50%' : 14,
          border: '2px solid var(--border-color, #e0e0e0)',
          backgroundColor: '#000',
        }}
      />
    </div>
  );
};

const RecordingSetupPanel: React.FC<RecordingSetupPanelProps> = ({
  onClose,
  recordingOptions,
  onOptionChange,
  onStartRecording,
}) => {
  const { t } = useTranslation();
  const handleOptionToggle = (option: 'recordScreen' | 'recordAudio' | 'recordCamera') => {
    onOptionChange({
      ...recordingOptions,
      [option]: !recordingOptions[option],
    });
  };
  const bubble = recordingOptions.cameraBubble ?? DEFAULT_CAMERA_BUBBLE;
  const setBubble = (patch: Partial<CameraBubble>) =>
    onOptionChange({ ...recordingOptions, cameraBubble: { ...bubble, ...patch } });

  return (
    <div 
      className="fixed right-6 top-1/2 transform -translate-y-1/2 rounded-lg shadow-xl w-80 z-50 overflow-y-auto"
      // Taller with the camera options: scroll instead of running off short screens.
      style={{ maxHeight: 'calc(100vh - 2rem)', backgroundColor: 'var(--card-bg, #ffffff)' }}
    >
      <div 
        className="flex items-center justify-between p-4 border-b"
        style={{
          borderColor: 'var(--border-color, #e0e0e0)',
        }}
      >
        <div className="flex items-center space-x-2">
          <Video 
            className="w-5 h-5" 
            style={{ color: 'var(--button-primary-bg, #1976d2)' }}
          />
          <h3 
            className="font-medium"
            style={{ color: 'var(--text-primary, #1a1a1a)' }}
          >
            {t('recording_setup.title', 'Recording')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close', 'Close')}
          className="rounded-md p-1.5 transition-colors"
          style={{
            color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--text-secondary, #525252)';
            e.currentTarget.style.backgroundColor =
              'var(--surface-hover, rgba(0, 0, 0, 0.08))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color =
              'var(--text-primary, var(--theme-foreground, #1a1a1a))';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <X className="w-5 h-5" strokeWidth={2} aria-hidden />
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <h4 
            className="text-sm font-medium mb-2"
            style={{ color: 'var(--text-primary, #1a1a1a)' }}
          >
            {t('recording_setup.what', 'What to record')}
          </h4>
          <button
            className="w-full flex items-center p-3 rounded-xl border"
            style={{
              border: recordingOptions.recordingMode === 'fullScreen'
                ? 'var(--recording-option-selected-border, 2px solid var(--button-primary-bg, #1976d2))'
                : 'var(--recording-option-border, 1px solid var(--border-color, #e0e0e0))',
              backgroundColor: recordingOptions.recordingMode === 'fullScreen'
                ? 'var(--recording-option-selected-bg, var(--card-active-bg, rgba(25, 118, 210, 0.1)))'
                : 'transparent',
              boxShadow: recordingOptions.recordingMode === 'fullScreen'
                ? 'var(--recording-option-selected-shadow, none)'
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (recordingOptions.recordingMode !== 'fullScreen') {
                e.currentTarget.style.border = 'var(--recording-option-hover-border, 1px solid var(--border-color-hover, #d0d0d0))';
                e.currentTarget.style.backgroundColor = 'var(--card-hover-bg, rgba(0, 0, 0, 0.05))';
              }
            }}
            onMouseLeave={(e) => {
              if (recordingOptions.recordingMode !== 'fullScreen') {
                e.currentTarget.style.border = 'var(--recording-option-border, 1px solid var(--border-color, #e0e0e0))';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => onOptionChange({...recordingOptions, recordingMode: 'fullScreen'})}
          >
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center mr-3"
              style={{
                backgroundColor: recordingOptions.recordingMode === 'fullScreen'
                  ? 'var(--recording-option-selected-icon, var(--button-primary-bg, #1976d2))'
                  : 'transparent',
                border: recordingOptions.recordingMode === 'fullScreen'
                  ? 'none'
                  : '2px solid var(--border-color, #e0e0e0)',
              }}
            >
              {recordingOptions.recordingMode === 'fullScreen' && (
                <Circle 
                  className="w-2.5 h-2.5" 
                  style={{ color: 'var(--button-primary-foreground, #ffffff)' }}
                />
              )}
            </div>
            <div className="text-left">
              <div 
                className="font-medium"
                style={{ 
                  color: recordingOptions.recordingMode === 'fullScreen'
                    ? 'var(--recording-option-selected-foreground, var(--recording-option-text, var(--text-primary, #1a1a1a)))'
                    : 'var(--recording-option-text, var(--text-primary, #1a1a1a))'
                }}
              >
                {t('recording_setup.screen', 'Screen, window or tab')}
              </div>
              <div 
                className="text-xs"
                style={{ 
                  color: recordingOptions.recordingMode === 'fullScreen'
                    ? 'var(--recording-option-selected-foreground, var(--recording-option-subtext, var(--text-primary, #1a1a1a)))'
                    : 'var(--recording-option-subtext, var(--text-secondary, #666666))',
                  opacity: 1,
                }}
              >
                {t('recording_setup.screen_hint', 'You choose which one in the next step')}
              </div>
            </div>
          </button>

          <button
            className="w-full flex items-center p-3 rounded-xl border"
            style={{
              border: recordingOptions.recordingMode === 'selectArea'
                ? 'var(--recording-option-selected-border, 2px solid var(--button-primary-bg, #1976d2))'
                : 'var(--recording-option-border, 1px solid var(--border-color, #e0e0e0))',
              backgroundColor: recordingOptions.recordingMode === 'selectArea'
                ? 'var(--recording-option-selected-bg, var(--card-active-bg, rgba(25, 118, 210, 0.1)))'
                : 'transparent',
              boxShadow: recordingOptions.recordingMode === 'selectArea'
                ? 'var(--recording-option-selected-shadow, none)'
                : 'none',
            }}
            onMouseEnter={(e) => {
              if (recordingOptions.recordingMode !== 'selectArea') {
                e.currentTarget.style.border = 'var(--recording-option-hover-border, 1px solid var(--border-color-hover, #d0d0d0))';
                e.currentTarget.style.backgroundColor = 'var(--card-hover-bg, rgba(0, 0, 0, 0.05))';
              }
            }}
            onMouseLeave={(e) => {
              if (recordingOptions.recordingMode !== 'selectArea') {
                e.currentTarget.style.border = 'var(--recording-option-border, 1px solid var(--border-color, #e0e0e0))';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onClick={() => onOptionChange({...recordingOptions, recordingMode: 'selectArea'})}
          >
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center mr-3"
              style={{
                backgroundColor: recordingOptions.recordingMode === 'selectArea'
                  ? 'var(--recording-option-selected-icon, var(--button-primary-bg, #1976d2))'
                  : 'transparent',
                border: recordingOptions.recordingMode === 'selectArea'
                  ? 'none'
                  : '2px solid var(--border-color, #e0e0e0)',
              }}
            >
              {recordingOptions.recordingMode === 'selectArea' && (
                <Circle 
                  className="w-2.5 h-2.5" 
                  style={{ color: 'var(--button-primary-foreground, #ffffff)' }}
                />
              )}
            </div>
            <div className="text-left">
              <div 
                className="font-medium"
                style={{ 
                  color: recordingOptions.recordingMode === 'selectArea'
                    ? 'var(--recording-option-selected-foreground, var(--recording-option-text, var(--text-primary, #1a1a1a)))'
                    : 'var(--recording-option-text, var(--text-primary, #1a1a1a))',
                }}
              >
                {t('recording_setup.area', 'Part of the screen')}
              </div>
              <div 
                className="text-xs"
                style={{ 
                  color: recordingOptions.recordingMode === 'selectArea'
                    ? 'var(--recording-option-selected-foreground, var(--recording-option-subtext, var(--text-primary, #1a1a1a)))'
                    : 'var(--recording-option-subtext, var(--text-secondary, #666666))',
                  opacity: 1,
                }}
              >
                {t('recording_setup.area_hint', 'Draw the area to record')}
              </div>
            </div>
          </button>
        </div>

        <div 
          className="flex items-center justify-between p-3 rounded-lg"
          style={{
            backgroundColor: 'var(--card-hover-bg, rgba(0, 0, 0, 0.05))',
          }}
        >
          <div className="flex items-center">
            <Mic 
              className="w-5 h-5 mr-2" 
              style={{ color: 'var(--text-primary, #1a1a1a)' }}
            />
            <span 
              className="text-sm"
              style={{ color: 'var(--text-primary, #1a1a1a)' }}
            >
              {t('no_microphone')}
            </span>
          </div>
          <Toggle
            checked={recordingOptions.recordAudio}
            onChange={() => handleOptionToggle('recordAudio')}
            label={t('no_microphone')}
          />
        </div>

        {/* Camera bubble — drawn into the recording */}
        <div
          className="p-3 rounded-lg space-y-3"
          style={{ backgroundColor: 'var(--card-hover-bg, rgba(0, 0, 0, 0.05))' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Camera className="w-5 h-5 mr-2" style={{ color: 'var(--text-primary, #1a1a1a)' }} />
              <span className="text-sm" style={{ color: 'var(--text-primary, #1a1a1a)' }}>
                {t('camera', 'Camera')}
              </span>
            </div>
            <Toggle
              checked={recordingOptions.recordCamera}
              onChange={() => handleOptionToggle('recordCamera')}
              label={t('camera', 'Camera')}
            />
          </div>

          {recordingOptions.recordCamera && (
            <>
              <CameraPreview shape={bubble.shape} />
              <OptionRow label={t('camera_shape', 'Shape')}>
                {(['circle', 'square'] as CameraShape[]).map((shape) => (
                  <Segment key={shape} selected={bubble.shape === shape} onClick={() => setBubble({ shape })}>
                    {shape === 'circle' ? t('camera_circle', 'Circle') : t('camera_square', 'Square')}
                  </Segment>
                ))}
              </OptionRow>
              <OptionRow label={t('camera_size', 'Size')}>
                {(['S', 'M', 'L'] as CameraSize[]).map((size) => (
                  <Segment key={size} selected={bubble.size === size} onClick={() => setBubble({ size })}>
                    {size}
                  </Segment>
                ))}
              </OptionRow>
              <OptionRow label={t('camera_position', 'Position')}>
                <div className="grid grid-cols-2 gap-1" role="group" aria-label={t('camera_position', 'Position')}>
                  {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as CameraCorner[]).map((corner) => {
                    const label = t(`camera_corner_${corner.replace('-', '_')}`, corner);
                    return (
                      <button
                        key={corner}
                        type="button"
                        onClick={() => setBubble({ corner })}
                        aria-pressed={bubble.corner === corner}
                        aria-label={label}
                        title={label}
                        className="w-9 h-6 rounded relative"
                        style={{
                          border: '1px solid var(--border-color, #e0e0e0)',
                          backgroundColor: 'var(--card-bg, #ffffff)',
                        }}
                      >
                        <span
                          className="absolute w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: bubble.corner === corner
                              ? 'var(--button-primary-bg, #1976d2)'
                              : 'var(--border-color, #c0c0c0)',
                            top: corner.startsWith('top') ? 3 : undefined,
                            bottom: corner.startsWith('bottom') ? 3 : undefined,
                            left: corner.endsWith('left') ? 3 : undefined,
                            right: corner.endsWith('right') ? 3 : undefined,
                          }}
                        />
                      </button>
                    );
                  })}
                </div>
              </OptionRow>
            </>
          )}
        </div>

        {/* Countdown before recording starts */}
        <div
          className="p-3 rounded-lg"
          style={{ backgroundColor: 'var(--card-hover-bg, rgba(0, 0, 0, 0.05))' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center">
              <Timer className="w-5 h-5 mr-2" style={{ color: 'var(--text-primary, #1a1a1a)' }} aria-hidden />
              <span className="text-sm" style={{ color: 'var(--text-primary, #1a1a1a)' }}>
                {t('countdown', 'Countdown')}
              </span>
            </div>
            <div className="flex items-center gap-1" role="group" aria-label={t('countdown', 'Countdown')}>
              {COUNTDOWN_OPTIONS.map((seconds) => (
                <Segment
                  key={seconds}
                  selected={(recordingOptions.countdownSeconds ?? DEFAULT_COUNTDOWN_SECONDS) === seconds}
                  onClick={() => onOptionChange({ ...recordingOptions, countdownSeconds: seconds })}
                >
                  {seconds === 0 ? t('countdown_off', 'Off') : t('countdown_seconds', { count: seconds, defaultValue: '{{count}} s' })}
                </Segment>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={onStartRecording}
          disabled={!recordingOptions.recordScreen && !recordingOptions.recordCamera}
          className="w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center"
          style={{
            backgroundColor: (recordingOptions.recordScreen || recordingOptions.recordCamera)
              ? 'var(--button-primary-bg, #1976d2)'
              : 'var(--button-primary-disabled-bg, #e0e0e0)',
            color: (recordingOptions.recordScreen || recordingOptions.recordCamera)
              ? 'var(--button-primary-foreground, #ffffff)'
              : 'var(--button-primary-disabled-foreground, #999999)',
            cursor: (recordingOptions.recordScreen || recordingOptions.recordCamera) ? 'pointer' : 'not-allowed',
          }}
          onMouseEnter={(e) => {
            if (recordingOptions.recordScreen || recordingOptions.recordCamera) {
              e.currentTarget.style.backgroundColor = 'var(--button-primary-hover-bg, #1565c0)';
            }
          }}
          onMouseLeave={(e) => {
            if (recordingOptions.recordScreen || recordingOptions.recordCamera) {
              e.currentTarget.style.backgroundColor = 'var(--button-primary-bg, #1976d2)';
            }
          }}
        >
          <Circle className="w-3.5 h-3.5 mr-2" fill="currentColor" aria-hidden />
          {t('start_recording')}
        </button>
      </div>
    </div>
  );
};

export default RecordingSetupPanel;
